/**
 * ChallengeManager — client-side state for async 1v1 challenge links.
 *
 * Create a challenge after completing a level, share the link,
 * and a friend plays the same seeded level for score comparison.
 *
 * API:
 *   createChallenge(opts)  → { id, challengeUrl }
 *   fetchChallenge(id)     → challenge object
 *   submitChallenge(id, opts) → { winner, ... }
 *   getPendingChallengeId() → id | null (from URL params)
 */

import XAuth from "./XAuth";

const ChallengeManager = {
  /**
   * Create a new challenge from a completed level.
   *
   * @param {Object} opts
   * @param {number} opts.celebrityIndex
   * @param {number} opts.seed
   * @param {number} opts.level
   * @param {number} opts.score
   * @param {number} opts.comboMax
   * @param {number} opts.kpm
   * @param {number} opts.timeSeconds
   * @returns {Promise<{id: string, challengeUrl: string}>}
   */
  async createChallenge(opts) {
    const token = XAuth.getSessionToken();
    if (!token) throw new Error("Not signed in");

    const resp = await fetch("/api/challenge", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(opts),
    });

    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error(err.error || "Failed to create challenge");
    }

    return resp.json();
  },

  /**
   * Fetch challenge details by ID.
   *
   * @param {string} id - 8-char hex challenge ID
   * @returns {Promise<Object>} challenge data
   */
  async fetchChallenge(id) {
    const resp = await fetch(`/api/challenge/${encodeURIComponent(id)}`);

    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error(err.error || "Challenge not found");
    }

    return resp.json();
  },

  /**
   * Submit a challenger's score.
   *
   * @param {string} id - challenge ID
   * @param {Object} opts
   * @param {number} opts.score
   * @param {number} opts.comboMax
   * @param {number} opts.kpm
   * @param {number} opts.timeSeconds
   * @returns {Promise<{winner: string, creatorScore: number, challengerScore: number}>}
   */
  async submitChallenge(id, opts) {
    const token = XAuth.getSessionToken();
    if (!token) throw new Error("Not signed in");

    const resp = await fetch(`/api/challenge/${encodeURIComponent(id)}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(opts),
    });

    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error(err.error || "Failed to submit challenge");
    }

    return resp.json();
  },

  /**
   * Check URL params for a pending challenge ID.
   * URL format: ?challenge=<8-char-hex>
   *
   * @returns {string|null}
   */
  getPendingChallengeId() {
    try {
      const params = new URLSearchParams(window.location.search);
      const id = params.get("challenge");
      if (id && /^[0-9a-f]{8}$/i.test(id)) return id;
    } catch {
      // Not in browser context
    }
    return null;
  },

  /**
   * Generate a shareable challenge URL.
   *
   * @param {string} id
   * @returns {string}
   */
  getShareUrl(id) {
    const base = window.location.origin || "https://howmuchwoodcouldawoodchuckchuck.com";
    return `${base}?challenge=${id}`;
  },

  /**
   * Generate share text for a challenge.
   *
   * @param {Object} challenge - from fetchChallenge()
   * @returns {string}
   */
  getShareText(challenge) {
    const score = (challenge.creatorScore || 0).toLocaleString();
    return [
      `I scored ${score} on Woodchuck! Can you beat me?`,
      "",
      ChallengeManager.getShareUrl(challenge.id),
    ].join("\n");
  },
};

export default ChallengeManager;
